/**
 * Pages in her log that their publisher has revised — the band at the top of the review queue.
 *
 * A source can announce the same URL twice. Usually that is a repeat, but the FCA's bulletins
 * distinguish "Published:" from "Last updated:", and a re-announcement carrying a later date is
 * the regulator saying the page itself changed.
 *
 * **What she does about it — Joanne, 16 September 2026.** Not "Seen it" and not "never for this
 * page": each update is shown with a summary of what changed, and three choices, all of which
 * update her log entry —
 *
 * - **Update log** puts her note in front of her Summary, stamped with the change date, and
 *   (unless she unticks it) files the item under the date the page now carries;
 * - **Update log + action** does the same and puts a next step on the Actions screen;
 * - **Stop tracking** does the same and then archives the item.
 *
 * There is no dismiss: these items are already in her log. The note and the step are editable
 * before she presses anything, because what reaches her log is hers — the model's summary is
 * there to help her decide and is dropped once she has.
 *
 * **Re-filing moves the item to another month, and says so first.** That is the one thing the
 * system never does on its own, and the only reason it is allowed here is that she is looking
 * at both dates and the month it will land in when she presses the button.
 */

import { useState } from "react";

import { ApiError } from "../api";
import { formatDate, monthLabel } from "../config";
import { updateActionDate, updateNextStep } from "../nextstep";
import type { ChangeSummary, Item, PageUpdate, PageUpdateDecision } from "../types";
import { ItemLink } from "./ItemLink";

const SIGNIFICANCE: Record<ChangeSummary["significance"], string> = {
  substantive: "Substantive change",
  minor: "Minor change",
  none_found: "No change of substance found",
  unclear: "Could not tell what changed",
};

/** "2026-09-01" as she reads it. The change date is a bare day, so no timezone is involved. */
function ukDay(day: string): string {
  const [year, month, date] = day.slice(0, 10).split("-");
  return year && month && date ? `${date}/${month}/${year}` : day;
}

interface Props {
  items: Item[];
  mutedCount: number;
  onDecide: (item: Item, update: PageUpdate) => Promise<void>;
  onRefresh: () => void;
}

export function ChangedPages({ items, mutedCount, onDecide, onRefresh }: Props) {
  if (!items.length) return null;
  const waiting = items.filter((item) => !item.change_summary).length;

  return (
    <section className="changed-band">
      {/* A div, not a <header>: the app's top bar is styled on the bare element, and that is
          what turned this line into a purple bar with amber text on it. */}
      <div className="changed-band-head">
        <strong>
          {items.length} page{items.length === 1 ? "" : "s"} in your log{" "}
          {items.length === 1 ? "has" : "have"} been updated
        </strong>
        <span className="muted">
          Choose what to do with each. Every choice updates the log entry; nothing changes until
          you press a button.
        </span>
        {waiting > 0 && (
          <button className="link" onClick={onRefresh}>
            check for {waiting === 1 ? "the summary" : `${waiting} summaries`} still being written
          </button>
        )}
      </div>
      <ul>
        {items.map((item) => (
          // Keyed on the summary too: when one arrives on a refresh, the card starts again from
          // its suggested wording rather than keeping the empty boxes it had while waiting.
          <ChangedPage
            key={`${item.id}:${item.change_summary?.generated_at ?? "waiting"}`}
            item={item}
            onDecide={onDecide}
          />
        ))}
      </ul>
      {mutedCount > 0 && (
        <footer className="muted">
          {mutedCount} page{mutedCount === 1 ? " is" : "s are"} muted and not shown here — listed on
          the Sources tab, where you can switch one back on.
        </footer>
      )}
    </section>
  );
}

function ChangedPage({
  item,
  onDecide,
}: {
  item: Item;
  onDecide: (item: Item, update: PageUpdate) => Promise<void>;
}) {
  const summary = item.change_summary ?? null;
  const changed = (item.page_changed_at || "").slice(0, 10);
  const changedUk = ukDay(changed);

  const [note, setNote] = useState(summary?.log_note ?? "");
  const [step, setStep] = useState(summary?.suggested_action || updateNextStep(changedUk));
  const [refile, setRefile] = useState(true);
  const [busy, setBusy] = useState<PageUpdateDecision | "">("");

  const fromMonth = item.month_key || (item.published_at || "").slice(0, 7);
  const toMonth = changed.slice(0, 7);
  const actionDate = updateActionDate(step, item);

  const decide = async (decision: PageUpdateDecision) => {
    const update: PageUpdate = { decision, changed_on: changed, note, refile };
    if (decision === "update_and_action") {
      update.next_step = step;
      if (actionDate.due_date !== undefined) update.due_date = actionDate.due_date;
    }
    setBusy(decision);
    try {
      await onDecide(item, update);
    } finally {
      setBusy("");
    }
  };

  return (
    <li className="changed-page">
      <div className="changed-head">
        <div className="changed-what">
          <ItemLink url={item.url}>{item.title}</ItemLink>
          <span className="muted small">{item.regulator}</span>
        </div>
        <div className="changed-when">
          {/* Both dates, always: "it changed" without "and you filed it under this" leaves her
              to go and look up the thing that makes it worth reading. */}
          Filed under <strong>{formatDate(item.published_at)}</strong> · the page is now dated{" "}
          <strong>{changedUk}</strong>
          {item.page_change_source ? (
            <span className="muted"> · noticed via {item.page_change_source}</span>
          ) : null}
        </div>
      </div>

      <ChangeSummaryBox summary={summary} />

      <div className="changed-edit">
        <label>
          <span>
            Note for the log <span className="muted small">— goes in front of your Summary as
            “Updated {changedUk}: …”. Leave it empty to add nothing.</span>
          </span>
          <textarea
            rows={2}
            value={note}
            maxLength={2000}
            placeholder={summary ? "" : "The suggested wording appears when the summary is ready."}
            onChange={(event) => setNote(event.target.value)}
          />
        </label>
        <label>
          <span>
            Action <span className="muted small">— used by Update log + action</span>
          </span>
          <textarea rows={2} value={step} onChange={(event) => setStep(event.target.value)} />
          {actionDate.read ? (
            <span className="muted small">
              Due {formatDate(actionDate.due_date)}, read from “{actionDate.read.basis}”
            </span>
          ) : actionDate.due_date === "" ? (
            <span className="muted small">
              Clears the old due date {formatDate(item.due_date)} — it belonged to a finished step.
            </span>
          ) : null}
        </label>
        {toMonth && (
          <label className="inline changed-refile">
            <input
              type="checkbox"
              checked={refile}
              onChange={(event) => setRefile(event.target.checked)}
            />
            File it under {changedUk}
            <span className="muted small">
              {fromMonth && fromMonth !== toMonth
                ? ` — moves it from ${monthLabel(fromMonth)} to ${monthLabel(toMonth)} in the log`
                : ` — it stays in ${monthLabel(toMonth)}`}
            </span>
          </label>
        )}
      </div>

      <div className="changed-do">
        <button
          className="primary"
          disabled={Boolean(busy)}
          title="Add the note to the log entry and keep tracking it"
          onClick={() => decide("update")}
        >
          {busy === "update" ? "Saving…" : "Update log"}
        </button>
        <button
          className="track-action"
          disabled={Boolean(busy) || !step.trim()}
          title="Update the log entry and put the action on the Actions screen"
          onClick={() => decide("update_and_action")}
        >
          {busy === "update_and_action" ? "Saving…" : "Update log + action"}
        </button>
        <button
          disabled={Boolean(busy)}
          title="Update the log entry, then archive it. It leaves the log and the Actions screen; nothing is deleted, and the Log's Archived filter brings it back."
          onClick={() => decide("stop_tracking")}
        >
          {busy === "stop_tracking" ? "Saving…" : "Stop tracking"}
        </button>
      </div>
    </li>
  );
}

function ChangeSummaryBox({ summary }: { summary: ChangeSummary | null }) {
  if (!summary) {
    return (
      <div className="change-summary pending muted">
        Summarising what changed — this usually takes a minute or two after an update arrives.
        You can decide without it.
      </div>
    );
  }
  if (summary.state === "unreadable") {
    return (
      <div className="change-summary unreadable">
        <strong>The page could not be read</strong>
        {summary.notes[0] ? `: ${summary.notes[0]}` : "."} Open the link to check it before
        deciding.
      </div>
    );
  }
  return (
    <div className="change-summary">
      <div className="change-summary-head">
        <span
          className="ai-badge"
          title={`Written by ${summary.model || "the assistant"}. A suggestion — nothing reaches your log unless you press a button.`}
        >
          AI
        </span>
        <strong>What changed</strong>
        <span className={`pill significance ${summary.significance}`}>
          {SIGNIFICANCE[summary.significance] ?? summary.significance}
        </span>
      </div>
      <ul>
        {summary.what_changed.map((point) => (
          <li key={point}>{point}</li>
        ))}
      </ul>
      <p className="muted small">
        Compared {summary.compared_against}.
        {summary.notes.length > 0 && ` Note: ${summary.notes.join("; ")}.`}
      </p>
    </div>
  );
}

/** A decision the API refused because the page moved on, or someone else decided first. */
export function isStaleDecision(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}
